import React from "react"
import * as RadixTabs from "@radix-ui/react-tabs"
import css from "@styled-system/css"

import { Box, Text } from "components"

/**
 * Tabs, built on Radix.
 *
 * Radix is controlled by `value`, not by index, so each tab needs a stable string id. The
 * index is used for that (`tab-0`, `tab-1`, …).
 *
 * **Active state comes from `[data-state="active"]`** — style off that attribute, never off a
 * prop. Radix does not forward an `isSelected` prop to the trigger, so any styling keyed to one
 * is silently dead code and every tab renders identically.
 */

const tabId = index => `tab-${index}`

const Tab = ({ value, children, ...rest }) => {
  if (!children) return null

  return (
    <RadixTabs.Trigger
      value={value}
      data-component-id="tab"
      css={css({
        position: "relative",
        m: 0,
        mr: "spacing.6",
        // No horizontal padding: the first tab has to sit flush with the section's
        // Wrapper edge, otherwise the tab row reads as indented from the heading and
        // fields beside it.
        px: 0,
        py: "spacing.3",
        border: 0,
        borderRadius: 0,
        borderBottom: "2px solid transparent",
        bg: "transparent",
        color: "alt",
        appearance: "none",
        cursor: "pointer",
        transition: "color 200ms ease, border-color 200ms ease",
        "&:last-of-type": { mr: 0 },
        "&:hover": { color: "text", borderBottomColor: "alt" },
        '&[data-state="active"]': {
          color: "text",
          borderBottomColor: "primary",
        },
      })}
      {...rest}
    >
      <Text
        data-component-id="tab.label"
        as="span"
        size={300}
        fontFamily="regular"
        fontWeight={600}
        textAlign="center"
        color="inherit"
        css={{ textTransform: "uppercase" }}
      >
        {children}
      </Text>
    </RadixTabs.Trigger>
  )
}

const Tabs = ({ tabs, alignTabs, ...rest }) => {
  const firstIndex = tabs.findIndex(Boolean)

  return (
    <RadixTabs.Root
      data-component-id="tabs"
      defaultValue={tabId(firstIndex === -1 ? 0 : firstIndex)}
      {...rest}
    >
      {/* Tabs */}
      <RadixTabs.List
        data-component-id="tabs.list"
        css={css({
          display: "flex",
          flexWrap: "wrap",
          justifyContent: alignTabs,
          width: "100%",
        })}
      >
        {tabs.map((tab, index) =>
          tab ? (
            <Tab key={index} value={tabId(index)}>
              {tab.label}
            </Tab>
          ) : null
        )}
      </RadixTabs.List>

      {/* Panels */}
      {tabs.map((tab, index) =>
        tab ? (
          <Box
            as={RadixTabs.Content}
            value={tabId(index)}
            data-component-id="tabs.panels.item"
            key={index}
            css={{ "&:focus": { outline: 0 } }}
          >
            {tab.content}
          </Box>
        ) : null
      )}
    </RadixTabs.Root>
  )
}

Tabs.defaultProps = {
  alignTabs: "flex-start",
}

export default Tabs
